import PropTypes from 'prop-types';
// @mui
import { Box, Typography, Link } from '@mui/material';
// components
import Breadcrumbs from './Breadcrumbs';

// compact (2026-07-04, table pages): ClickUp-style header — no big H4 title; the small
// breadcrumb trail IS the page identity, with the current (last) crumb emphasised.
// Non-compact pages render exactly as before (title + trail).
const HeaderBreadcrumbs = ({ links, action, heading, compact = false, moreLink = '' || [], sx, ...other }) => (
    <Box sx={{ mb: other?.smallMargin ?? 3, ...sx }}>
        {/* data-page-header: the call pill sits just below the page's header row (CU-869fmcc9r). */}
        <Box data-page-header sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
                sx={{
                    flexGrow: 1,
                    // Emphasise the current page in compact mode: the trail's last item
                    // doubles as the page title (ClickUp pattern).
                    ...(compact && {
                        '& .MuiBreadcrumbs-li:last-of-type .MuiTypography-root': {
                            color: 'text.primary',
                            fontWeight: 600,
                            fontSize: '0.9375rem',
                        },
                    }),
                }}
                className={heading === 'Patient Feedback' ? 'appt-review-table' : ''}
            >
                {compact ? (
                    // Screen readers still need a page heading even though the visual
                    // title is the emphasised last crumb (visually-hidden h1 pattern).
                    <Typography
                        component='h1'
                        sx={{
                            position: 'absolute',
                            width: '1px',
                            height: '1px',
                            margin: '-1px',
                            padding: 0,
                            border: 0,
                            overflow: 'hidden',
                            clip: 'rect(0 0 0 0)',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {heading}
                    </Typography>
                ) : (
                    <Typography variant='h4' gutterBottom>
                        {heading}
                    </Typography>
                )}
                {/* Trail is optional: Breadcrumbs crashes on undefined links, hence the guard. */}
                {links?.length > 0 && <Breadcrumbs links={links} {...other} />}
            </Box>

            {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
        </Box>

        {((typeof moreLink === 'string' && moreLink) || (Array.isArray(moreLink) && moreLink.length > 0)) && (
            <Box sx={{ mt: 2 }}>
                {typeof moreLink === 'string' ? (
                    <Link href={moreLink} target='_blank' rel='noopener' variant='body2'>
                        {moreLink}
                    </Link>
                ) : (
                    moreLink.map((href) => (
                        <Link
                            noWrap
                            key={href}
                            href={href}
                            variant='body2'
                            target='_blank'
                            rel='noopener'
                            sx={{ display: 'table' }}
                        >
                            {href}
                        </Link>
                    ))
                )}
            </Box>
        )}
    </Box>
);

HeaderBreadcrumbs.propTypes = {
    links: PropTypes.array,
    compact: PropTypes.bool,
    action: PropTypes.node,
    heading: PropTypes.string.isRequired,
    moreLink: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    sx: PropTypes.object,
};

export default HeaderBreadcrumbs;